/**
 * @module NotificationsReactStyles
 * @package @geenius/notifications-react
 * @description Centralizes reusable Tailwind class variants and accent helpers
 * used by the React notifications components and pages. Keeping these rules in
 * one place prevents the same visual logic from being re-encoded across every
 * bell, badge, row, and filter control.
 */

import type { NotificationType } from '@geenius/notifications/shared'
import { cva } from 'class-variance-authority'
import type { CSSProperties } from 'react'

/**
 * CSS custom properties used to express notification-type accents.
 */
export interface NotificationAccentStyle extends CSSProperties {
  '--gn-notification-accent'?: string
  '--gn-notification-accent-soft'?: string
  '--gn-notification-accent-border'?: string
}

type NotificationAccentTokens = {
  '--gn-notification-accent': string
  '--gn-notification-accent-soft': string
  '--gn-notification-accent-border': string
}

const NOTIFICATION_ACCENT_TOKENS: Record<
  NotificationType,
  NotificationAccentTokens
> = {
  info: {
    '--gn-notification-accent': 'var(--gn-notification-info)',
    '--gn-notification-accent-soft': 'var(--gn-notification-info-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-info-border)',
  },
  success: {
    '--gn-notification-accent': 'var(--gn-notification-success)',
    '--gn-notification-accent-soft': 'var(--gn-notification-success-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-success-border)',
  },
  warning: {
    '--gn-notification-accent': 'var(--gn-notification-warning)',
    '--gn-notification-accent-soft': 'var(--gn-notification-warning-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-warning-border)',
  },
  error: {
    '--gn-notification-accent': 'var(--gn-notification-error)',
    '--gn-notification-accent-soft': 'var(--gn-notification-error-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-error-border)',
  },
  mention: {
    '--gn-notification-accent': 'var(--gn-notification-mention)',
    '--gn-notification-accent-soft': 'var(--gn-notification-mention-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-mention-border)',
  },
  invite: {
    '--gn-notification-accent': 'var(--gn-notification-invite)',
    '--gn-notification-accent-soft': 'var(--gn-notification-invite-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-invite-border)',
  },
  payment: {
    '--gn-notification-accent': 'var(--gn-notification-payment)',
    '--gn-notification-accent-soft': 'var(--gn-notification-payment-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-payment-border)',
  },
  system: {
    '--gn-notification-accent': 'var(--gn-notification-system)',
    '--gn-notification-accent-soft': 'var(--gn-notification-system-soft)',
    '--gn-notification-accent-border': 'var(--gn-notification-system-border)',
  },
}

/**
 * Returns CSS custom properties for a notification type accent.
 *
 * @param type Notification type whose accent should be resolved.
 * @returns Custom properties consumed by Tailwind arbitrary-value classes.
 */
export function getNotificationAccentStyle(
  type: NotificationType,
): NotificationAccentStyle {
  return NOTIFICATION_ACCENT_TOKENS[type]
}

/**
 * Shared badge styling used by notification type chips.
 */
export const notificationTypeBadgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border border-[var(--gn-notification-accent-border)] bg-[var(--gn-notification-accent-soft)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--gn-notification-accent)]',
)

/**
 * Shared frame styling used by notification icon fallbacks.
 */
export const notificationIconFrameVariants = cva(
  'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-[var(--gn-notification-accent-border)] bg-[var(--gn-notification-accent-soft)] text-sm text-[var(--gn-notification-accent)] shadow-sm',
)

/**
 * Button styling used by status toggle controls.
 */
export const notificationToggleVariants = cva(
  'rounded-lg border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
  {
    variants: {
      active: {
        true: 'border-primary/30 bg-primary/10 text-primary shadow-sm',
        false:
          'border-[var(--gn-notifications-panel-border)] bg-transparent text-muted-foreground hover:border-[var(--gn-notifications-panel-border)] hover:bg-[var(--gn-notifications-panel-surface-muted)] hover:text-foreground',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
)

/**
 * Button styling used by type-specific filter chips.
 */
export const notificationTypeFilterVariants = cva(
  'rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
  {
    variants: {
      active: {
        true:
          'border-[var(--gn-notification-accent-border)] bg-[var(--gn-notification-accent-soft)] text-[var(--gn-notification-accent)] shadow-sm',
        false:
          'border-transparent bg-transparent text-muted-foreground hover:border-[var(--gn-notifications-panel-border)] hover:bg-[var(--gn-notifications-panel-surface-muted)] hover:text-foreground',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
)
